<template>
  <div class="benchmark-filter">
    <singleSelect
      v-model="selectionModel"
      track-by="key"
      :no-border="false"
      label="label"
      :top-left-label="filterMeta?.name"
      :top-right-label="selectionModel?.label"
      :header-label="selectionRangeText"
      :disabled="loading"
      :loading="loading"
      :options="validDropdownOptions"
      @input="updateSelectionModel" />
    <!-- Picker host for the Custom Range option: the same calendar component
         the main date control uses, with its own trigger hidden. Opened
         programmatically from updateSelectionModel. -->
    <div
      v-if="$hasFeatureFlag('CUSTOM_BENCHMARK_RANGES')"
      class="benchmark-custom-range-host">
      <time-range-filter
        ref="customRangePicker"
        hide-icon
        hide-secondary-label
        no-border
        @submit="applyCustomRange">
        <span />
      </time-range-filter>
    </div>
    <!-- Editor host for the Rolling Average option, opened programmatically
         from updateSelectionModel the same way as the Custom Range picker. -->
    <div
      v-if="rollingAverageEnabled"
      class="benchmark-rolling-average-host">
      <b-dropdown
        ref="rollingAverageDropdown"
        aria-role="menu"
        min-height="auto"
        trap-focus>
        <template #trigger>
          <span />
        </template>
        <b-dropdown-item
          aria-role="menu-item"
          custom
          paddingless>
          <form
            class="rolling-average-editor"
            @submit.prevent="applyRollingAverage">
            <div class="rolling-average-fields">
              <span>Average over</span>
              <b-numberinput
                v-model="pendingRepeatCount"
                :min="2"
                :step="1"
                size="is-small"
                controls-alignment="right" />
              <span>periods of</span>
              <b-numberinput
                v-model="pendingRepeatPeriodN"
                :min="1"
                :step="1"
                size="is-small"
                controls-alignment="right" />
              <b-select
                v-model="pendingRepeatUnit"
                size="is-small">
                <option value="week">
                  weeks
                </option>
                <option value="day">
                  days
                </option>
              </b-select>
            </div>
            <p
              v-if="isPendingRepeatValid"
              class="rolling-average-summary">
              {{ pendingRepeatSummary }}
            </p>
            <footer class="rolling-average-footer">
              <b-button
                size="is-small"
                @click="closeRollingAverageEditor">
                Cancel
              </b-button>
              <b-button
                type="is-primary"
                size="is-small"
                native-type="submit"
                :disabled="!isPendingRepeatValid">
                Apply
              </b-button>
            </footer>
          </form>
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </div>
</template>

<script>
  import singleSelect from '@/components/ui/singleselect'
  import TimeRangeFilter from '@/components/controls/filter/TimeRangeFilter.vue'
  import * as moment from 'moment'

  const CUSTOM_RANGE_KEY = '__custom_range__'
  const ROLLING_AVERAGE_KEY = '__rolling_average__'
  const DEFAULT_REPEAT_COUNT = 4
  const DEFAULT_REPEAT_PERIOD_N = 1
  const DEFAULT_REPEAT_UNIT = 'week'

  export default {
    name: 'DashboardBenchmarkFilter',
    components: {
      singleSelect,
      TimeRangeFilter,
    },
    mixins: [],
    props: {
      filterMeta: {
        type: Object,
        required: true,
      },
      value: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        selectionModel: null,
        loading: false,
        dropdownOptions: [],
        pendingCustomOption: null,
        pendingRepeatCount: DEFAULT_REPEAT_COUNT,
        pendingRepeatPeriodN: DEFAULT_REPEAT_PERIOD_N,
        pendingRepeatUnit: DEFAULT_REPEAT_UNIT,
      }
    },

    computed: {
      dataContext: {
        get () {
          return this.value
        },
        set (newValue) {
          this.$emit('input', newValue)
        },
      },
      validDropdownOptions () {
        return this.dropdownOptions.filter((option) => {
          if (
            option.key === CUSTOM_RANGE_KEY &&
            !this.$hasFeatureFlag('CUSTOM_BENCHMARK_RANGES')
          ) {
            return false
          }
          if (option.key === ROLLING_AVERAGE_KEY && !this.rollingAverageEnabled) {
            return false
          }
          return this.isOffsetViable(option)
        })
      },
      rollingAverageEnabled () {
        return this.$hasFeatureFlag('CUSTOM_BENCHMARK_RANGES') && this.$hasFeatureFlag('USE_SNAPQL')
      },
      // Custom Range and Rolling Average are excluded: falling back to either
      // would open its editor unprompted.
      fallbackOptions () {
        return this.validDropdownOptions.filter(
          (option) => option.key !== CUSTOM_RANGE_KEY && option.key !== ROLLING_AVERAGE_KEY
        )
      },
      defaultOption () {
        return this.dropdownOptions.find(
          (o) => o.key === this.filterMeta.default_option
        )
      },
      customRangeDates () {
        const selection = this.dataContext?.control_selections?.[this.filterMeta.key]
        if (selection?.selected_option !== CUSTOM_RANGE_KEY) return []
        return [...(selection.selected_dates || []),].sort()
      },
      rollingAverageRepeat () {
        const selection = this.dataContext?.control_selections?.[this.filterMeta.key]
        if (selection?.selected_option !== ROLLING_AVERAGE_KEY) return null
        return selection.repeat
      },
      isPendingRepeatValid () {
        return (
          Number.isInteger(this.pendingRepeatCount) &&
          this.pendingRepeatCount >= 2 &&
          Number.isInteger(this.pendingRepeatPeriodN) &&
          this.pendingRepeatPeriodN >= 1
        )
      },
      // Plain-English list of the days read, e.g. count 3 of 52 weeks:
      // "Compares each day with the same day 52, 104 and 156 weeks earlier."
      pendingRepeatSummary () {
        const count = this.pendingRepeatCount
        const periodN = this.pendingRepeatPeriodN
        const unitLabel = `${this.pendingRepeatUnit}s`
        if (this.pendingRepeatUnit === 'day' && periodN === 1) {
          return `Compares each day with each of the ${count} days before it.`
        }
        const offsets = []
        for (let step = 1; step <= count; step++) {
          offsets.push(step * periodN)
        }
        let offsetsText = `${offsets.slice(0, -1).join(', ')} and ${offsets[offsets.length - 1]}`
        if (count > 6) {
          offsetsText = `${offsets.slice(0, 3).join(', ')} … ${offsets[offsets.length - 1]}`
        }
        return `Compares each day with the same day ${offsetsText} ${unitLabel} earlier.`
      },
      benchmarkDateRange () {
        const selectedDates = this.dataContext?.date_selection?.selected_dates
        const offsetDays = this.selectionModel?.offset_days
        if (
          selectedDates.length > 0 &&
          offsetDays
        ) {
          const startDate = moment(selectedDates[0])
            .add(offsetDays, 'days')
            .format('ll')
          const endDate = moment(selectedDates[selectedDates.length - 1])
            .add(offsetDays, 'days')
            .format('ll')
          return { startDate, endDate, }
        }
        return { startDate: null, endDate: null, }
      },
      selectionRangeText () {
        if (!this.selectionModel) return ''

        if (this.selectionModel.key === CUSTOM_RANGE_KEY) {
          if (this.customRangeDates.length === 0) return ''
          const startDate = moment(this.customRangeDates[0]).format('ll')
          const endDate = moment(
            this.customRangeDates[this.customRangeDates.length - 1]
          ).format('ll')
          return `${startDate} ${endDate !== startDate ? ` to ${endDate}` : ''}`
        }

        if (this.selectionModel.key === ROLLING_AVERAGE_KEY) {
          if (!this.rollingAverageRepeat) return ''
          const { count, period, } = this.rollingAverageRepeat
          if (period.n > 1) return `${count} × ${period.n} ${period.unit}s`
          return `last ${count} ${period.unit}s`
        }

        const startDate = this.benchmarkDateRange.startDate
        const endDate = this.benchmarkDateRange.endDate

        if (!startDate) return ''

        return `${startDate} ${
          endDate && endDate != startDate ? ` to ${endDate}` : ''
        }`
      },
    },
    watch: {
      validDropdownOptions: {
        handler () {

          //when the options are changed, make sure the selection is still valid if not reset to default is viable else first available option
          if (
            this.selectionModel &&
            !this.isOffsetViable(this.selectionModel)
          ) {
            if (this.isOffsetViable(this.defaultOption)) {
              this.updateSelectionModel(this.defaultOption)
              this.selectionModel = this.defaultOption
            } else {
              this.updateSelectionModel(this.fallbackOptions[0])
              this.selectionModel = this.fallbackOptions[0]
            }
          }
        },
      },
    },

    async mounted () {
      this.dropdownOptions = [...this.filterMeta.options,]
      const defaultOption = this.defaultOption || this.fallbackOptions[0]
      // Set default selection only if there's no user selection
      if (!this.dataContext.control_selections[this.filterMeta.key]) {
        this.updateSelectionModel(defaultOption)
        this.selectionModel = defaultOption
      }
    },

    methods: {
      isOffsetViable (option) {
        const offsetDays = Math.abs(option.offset_days || 0)
        if (!offsetDays) {
          return true
        }
        let start = moment(this.dataContext.date_selection.selected_dates[0])
        let end = moment(
          this.dataContext.date_selection.selected_dates[
            this.dataContext.date_selection.selected_dates.length - 1
          ]
        )
        let durationDays = moment(end).diff(moment(start), 'days') + 1
        return offsetDays >= durationDays
      },
      updateSelectionModel (value) {
        if (value.key === CUSTOM_RANGE_KEY) {
          // Nothing is written until the picker's Apply: v-model already moved
          // the dropdown to Custom Range, so put it back to what's applied.
          this.restoreAppliedOption()
          this.openCustomRangePicker(value)
          return
        }
        if (value.key === ROLLING_AVERAGE_KEY) {
          // Same as Custom Range: nothing is written until the editor's Apply.
          this.restoreAppliedOption()
          this.openRollingAverageEditor()
          return
        }
        this.$set(this.dataContext.control_selections, this.filterMeta.key, {
          type: this.filterMeta.type,
          selected_option: value.key,
        })
      },
      restoreAppliedOption () {
        const appliedKey = this.dataContext.control_selections[this.filterMeta.key]?.selected_option
        this.selectionModel = this.dropdownOptions.find((o) => o.key === appliedKey) || this.selectionModel
      },
      openCustomRangePicker (option) {
        this.pendingCustomOption = option
        // TimeRangeFilter has no public open method; its own trigger is hidden
        // here, so toggle its b-dropdown the way the component itself does.
        this.$refs.customRangePicker.$refs['calendar-dropdown'].toggle()
      },
      applyCustomRange (data) {
        let dates = data.dates
        if (data.mode === 'RANGE') {
          dates = this.expandRange(data.dates[0], data.dates[1])
        }
        this.$set(this.dataContext.control_selections, this.filterMeta.key, {
          type: this.filterMeta.type,
          selected_option: CUSTOM_RANGE_KEY,
          selected_dates: dates,
        })
        this.selectionModel = this.pendingCustomOption ||
          this.dropdownOptions.find((o) => o.key === CUSTOM_RANGE_KEY)
      },
      openRollingAverageEditor () {
        this.pendingRepeatCount = this.rollingAverageRepeat?.count || DEFAULT_REPEAT_COUNT
        this.pendingRepeatPeriodN = this.rollingAverageRepeat?.period.n || DEFAULT_REPEAT_PERIOD_N
        this.pendingRepeatUnit = this.rollingAverageRepeat?.period.unit || DEFAULT_REPEAT_UNIT
        this.$refs.rollingAverageDropdown.toggle()
      },
      closeRollingAverageEditor () {
        this.$refs.rollingAverageDropdown.toggle()
      },
      applyRollingAverage () {
        this.$set(this.dataContext.control_selections, this.filterMeta.key, {
          type: this.filterMeta.type,
          selected_option: ROLLING_AVERAGE_KEY,
          selected_dates: [],
          repeat: {
            count: this.pendingRepeatCount,
            period: { n: this.pendingRepeatPeriodN, unit: this.pendingRepeatUnit, },
          },
        })
        this.selectionModel = this.dropdownOptions.find((o) => o.key === ROLLING_AVERAGE_KEY)
        this.closeRollingAverageEditor()
      },
      expandRange (start, end) {
        const dates = []
        let current = moment(start)
        const endDate = moment(end)
        while (current.isSameOrBefore(endDate, 'day')) {
          dates.push(current.format('YYYY-MM-DD'))
          current = current.add(1, 'day')
        }
        return dates
      },
    },
  }
</script>
<style lang="scss" scoped>
::v-deep .dropdown-menu {
  padding-top: 4px !important;
  .dropdown-content {
    border-radius: 6px !important;
  }
}

.benchmark-filter {
  position: relative;
}

// The picker host anchors the calendar dropdown under the control while its
// own trigger stays invisible and unclickable.
.benchmark-custom-range-host {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;

  ::v-deep .datepicker-button {
    display: block;
    height: 0;
    padding: 0 !important;
    border: none !important;
    overflow: hidden;
    pointer-events: none;
  }
}

.benchmark-rolling-average-host {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}

.rolling-average-editor {
  padding: 12px;
  width: 320px;

  .rolling-average-fields {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    white-space: nowrap;
  }

  .rolling-average-summary {
    margin-top: 8px;
    font-size: 12px;
    color: #7e7d84;
  }

  .rolling-average-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }
}
</style>
